import type { TourService, TourStudio } from './tour.service';

/**
 * One stop of the tour. `target` is a data-tour name in the page, or finds the element; the note
 * points at it. A console step has Back and Next. A test step moves on by itself once `done`, or
 * has a `next` button; `stuck` is what to say meanwhile, when there is something to say.
 */
export interface TourStep {
  part: 'console' | 'test';
  target: string | ((t: TourService) => Element | null);
  text: string | ((t: TourService) => string);
  enter?: (t: TourService) => unknown;
  done?: (t: TourService) => boolean;
  stuck?: (t: TourService) => string | false | null | undefined;
  next?: string;
  adminOnly?: boolean;
}

// MockHR, our demo HR app, as in the desktop's tour: safe to practise on. On the web it signs in
// with a login profile from Settings, which the team needs for it. The request is one a saved
// test proves it can do: navigate, type, check.
export const TOUR_SITE = 'd3c6dg8ph04cp2.cloudfront.net';
const TOUR_ASK = 'Go to Employees, search for David and check that David Chen is in the results';

const $ = (name: string) => document.querySelector(`[data-tour="${name}"]`);
const host = (url: string | null) => { try { return url ? new URL(url).host : ''; } catch { return ''; } };
const last = (s: TourStudio, test: (m: TourStudio['chat'][number]) => boolean) => [...s.chat].reverse().find(test);
const asked = (s: TourStudio) => s.chat.some((m) => m.role === 'tester' && m.asked);
const replayed = (s: TourStudio) => last(s, (m) => m.kind === 'replay' || /^Replaying |^All \d+ steps? passed|^Step \d+ failed/.test(m.text || ''));
const noProfile = (s: TourStudio) => !!last(s, (m) => m.role === 'optimus' && /no login profile for this site/.test(m.text || ''));
/** The buttons Optimus offered last — the accounts to sign in with, once they arrive. */
const choices = () => {
  const feet = Array.from(document.querySelectorAll('.chat-log .chat-msg.optimus footer'));
  return feet.reverse().find((f) => f.querySelector('.chat-chip:not(:disabled)')) || null;
};

export const TOUR_STEPS: TourStep[] = [
  // ── the console ──
  { part: 'console', target: 'tab-dashboard', enter: (t) => t.show('/admin/dashboard'),
    text: 'Dashboard — your team\'s tests and how they did last time. Open a project for its suites: run one now in the '
      + 'cloud, or click a test for its report, with screenshots of every run.' },
  { part: 'console', target: 'tab-authoring', enter: (t) => t.show('/admin/studio-chat'),
    text: 'Authoring — where tests are made. Studio chat builds one by chatting with Optimus AI; Auto-Suite explores a '
      + 'site and writes a whole suite; API Suite makes tests from an API\'s spec.' },
  { part: 'console', target: 'tab-schedule', enter: (t) => t.show('/admin/schedule'),
    text: 'Schedule — suites run by themselves, on the days and at the times you choose.' },
  { part: 'console', target: 'tab-runs', enter: (t) => t.show('/admin/runs'),
    text: 'Reports — every run of every suite: what passed, what failed and why, with screenshots. You can also send '
      + 'the results to Azure DevOps Test Plans from here.' },
  { part: 'console', target: 'tab-users', adminOnly: true, enter: (t) => t.show('/admin/users'),
    text: 'Users — invite your team and choose what each person can do: view results, build tests, or run the console.' },
  { part: 'console', target: 'settings', enter: (t) => t.show('/admin/settings'),
    text: 'Settings — login profiles, which say how tests sign in to each site (passwords are stored securely, never in a '
      + 'test), and the connections to Jira and Azure DevOps.' },
  { part: 'console', target: 'desktop',
    text: 'Optimus Desktop — the same Studio, with a live browser beside you: watch Optimus work, record steps yourself, '
      + 'and test sites only your computer can reach. Download it here, for Mac.' },

  // ── your first test, in Studio chat ──
  { part: 'test', target: 'send',
    enter: async (t) => {
      const s = await t.openStudio();
      if (!s) { return; }
      if (s.site || s.steps.length || s.chat.some((m) => m.role === 'tester')) { s.newTest(); }
      s.message = TOUR_SITE;
    },
    text: 'Now let\'s build a test together, on a demo HR app that\'s safe to practise on. This box is where you talk to '
      + 'Optimus AI — we\'ve typed the demo\'s address. Press Send.',
    stuck: ({ studio: s }) => s && (s.busy ? 'Optimus is opening the demo in its browser — a moment…'
      : !s.site && last(s, (m) => m.role === 'optimus')?.kind === 'error'
        && 'Optimus couldn\'t open the demo — see the message. You can skip the tour and take it again from your account menu.'),
    done: ({ studio: s }) => !!s && host(s.site) === TOUR_SITE && !s.busy },
  { part: 'test', target: () => choices() || $('chat-log'),
    text: 'Optimus asks how the test signs in. Pick one of the accounts it offers — for this demo, the admin one: the '
      + 'test goes to the employee list, which only admins see.',
    stuck: ({ studio: s }) => s && (s.busy ? 'Signing in — a moment…'
      : noProfile(s) ? 'Your team has no login profile for the demo yet, so the test can\'t sign in. An admin adds one in '
        + 'Settings → Login Profiles; then take the tour again from your account menu.'
      : last(s, (m) => m.role === 'optimus')?.kind === 'error' ? 'That sign-in didn\'t work — see the message above. You can '
        + 'skip the tour and take it again from your account menu.'
      : !s.authProfileId && last(s, (m) => m.role === 'tester')?.text === 'No sign-in needed'
        && 'The demo has to be signed in to reach its employees. Press New test at the top to start again, and pick an account.'),
    done: ({ studio: s }) => !!s && !!s.authProfileId && !s.busy },
  { part: 'test', target: ({ studio: s }) => (s?.busy ? $('test') : $('send')),   // out of the way while it works
    enter: ({ studio: s }) => { if (s) { s.message = TOUR_ASK; } },
    text: 'Now say what the test should do, in plain words. We\'ve written one for you — press Send and watch the page '
      + 'on the right.',
    stuck: ({ studio: s }) => s && (s.busy && asked(s) ? 'Optimus AI is doing it in its browser now. Each step that works '
      + 'joins the test above.'
      : asked(s) && s.steps.length < 2 && 'Optimus AI needs a little more — answer in the box, as you would reply to a colleague.'),
    done: ({ studio: s }) => !!s && asked(s) && !s.busy && s.steps.length >= 2 },
  { part: 'test', target: 'test', next: 'Next',
    text: ({ studio: s }) => (s?.testOpen ? 'These are the steps it wrote. Remove any you don\'t want with ×, or ask for more '
      + 'in the chat.' : 'This is the test it wrote — click it to see its steps.') },
  { part: 'test', target: 'replay',
    text: 'Replay runs the test again from the start, to prove it works every time. Press Replay.',
    stuck: ({ studio: s }) => s && (s.busy && replayed(s) ? 'Replaying — each step is ticked as it passes.'
      : !s.busy && /^Step \d+ failed/.test(replayed(s)?.text || '')
        && 'A step failed on replay — press “Fix it with AI” in the chat, then Replay again.'),
    done: ({ studio: s }) => !!s && !s.busy && /^All \d+ steps? passed/.test(replayed(s)?.text || '') },
  { part: 'test', target: 'save', next: 'Finish',
    text: 'Every step passed — that\'s a working test. Save keeps it in Optimus, where your team runs it from the '
      + 'Dashboard or on a schedule. This one is a demo, so saving it is up to you.' },
];
